export interface EmailTheme {
  id?: string;
  name?: string;
  primary: string;
  canvasBg: string;
  bodyBg: string;
  bodyText: string;
  mutedText: string;
  linkColor: string;
  sectionBg: string;
  footerBg: string;
  footerText: string;
  headerText: string;
}

export interface ColorThemePreset extends EmailTheme {
  id: string;
  name: string;
  description?: string;
  mode: "light" | "dark";
}

export const DEFAULT_THEME: EmailTheme = {
  primary: "#3730a3",
  canvasBg: "#eff4ff",
  bodyBg: "#ffffff",
  bodyText: "#0f172a",
  mutedText: "#64748b",
  linkColor: "#3730a3",
  sectionBg: "#eff4ff",
  footerBg: "#f8fafc",
  footerText: "#64748b",
  headerText: "#ffffff",
};

export const COLOR_THEMES: ColorThemePreset[] = [
  {
    id: "indigo",
    name: "Blue",
    description: "Default — authentication, generic",
    mode: "light",
    primary: "#3730a3",
    canvasBg: "#eff4ff",
    bodyBg: "#ffffff",
    bodyText: "#0f172a",
    mutedText: "#64748b",
    linkColor: "#3730a3",
    sectionBg: "#eff4ff",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "emerald",
    name: "Emerald",
    description: "Success, approvals",
    mode: "light",
    primary: "#047857",
    canvasBg: "#ecfdf5",
    bodyBg: "#ffffff",
    bodyText: "#064e3b",
    mutedText: "#64748b",
    linkColor: "#047857",
    sectionBg: "#ecfdf5",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "crimson",
    name: "Crimson",
    description: "Urgent, errors",
    mode: "light",
    primary: "#dc2626",
    canvasBg: "#fef2f2",
    bodyBg: "#ffffff",
    bodyText: "#7f1d1d",
    mutedText: "#64748b",
    linkColor: "#dc2626",
    sectionBg: "#fef2f2",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "amber",
    name: "Amber",
    description: "Warnings, reminders",
    mode: "light",
    primary: "#b45309",
    canvasBg: "#fffbeb",
    bodyBg: "#ffffff",
    bodyText: "#78350f",
    mutedText: "#64748b",
    linkColor: "#b45309",
    sectionBg: "#fffbeb",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "slate",
    name: "Slate",
    description: "Neutral, audit",
    mode: "light",
    primary: "#475569",
    canvasBg: "#f8fafc",
    bodyBg: "#ffffff",
    bodyText: "#1e293b",
    mutedText: "#64748b",
    linkColor: "#475569",
    sectionBg: "#f8fafc",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "violet",
    name: "Violet",
    description: "Automation, AI",
    mode: "light",
    primary: "#6d28d9",
    canvasBg: "#f5f3ff",
    bodyBg: "#ffffff",
    bodyText: "#4c1d95",
    mutedText: "#64748b",
    linkColor: "#6d28d9",
    sectionBg: "#f5f3ff",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "teal",
    name: "Teal",
    description: "Notifications",
    mode: "light",
    primary: "#0f766e",
    canvasBg: "#f0fdfa",
    bodyBg: "#ffffff",
    bodyText: "#134e4a",
    mutedText: "#64748b",
    linkColor: "#0f766e",
    sectionBg: "#f0fdfa",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "rose",
    name: "Rose",
    description: "Friendly alerts",
    mode: "light",
    primary: "#be123c",
    canvasBg: "#fff1f2",
    bodyBg: "#ffffff",
    bodyText: "#831843",
    mutedText: "#64748b",
    linkColor: "#be123c",
    sectionBg: "#fff1f2",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "sky",
    name: "Sky",
    description: "Calm, gentle",
    mode: "light",
    primary: "#0369a1",
    canvasBg: "#f0f9ff",
    bodyBg: "#ffffff",
    bodyText: "#0c4a6e",
    mutedText: "#64748b",
    linkColor: "#0369a1",
    sectionBg: "#f0f9ff",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
  {
    id: "zinc",
    name: "Charcoal",
    description: "Reports, sophisticated",
    mode: "light",
    primary: "#27272a",
    canvasBg: "#fafafa",
    bodyBg: "#ffffff",
    bodyText: "#18181b",
    mutedText: "#64748b",
    linkColor: "#27272a",
    sectionBg: "#fafafa",
    footerBg: "#f8fafc",
    footerText: "#64748b",
    headerText: "#ffffff",
  },
];

const COMPANY_THEME_STORAGE_KEY = "email-templates:company-theme";

export const loadCompanyTheme = (): string | null => {
  try {
    return localStorage.getItem(COMPANY_THEME_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const saveCompanyTheme = (themeId: string): void => {
  try {
    if (themeId) {
      localStorage.setItem(COMPANY_THEME_STORAGE_KEY, themeId);
    } else {
      localStorage.removeItem(COMPANY_THEME_STORAGE_KEY);
    }
  } catch {
    /* noop */
  }
};

export const getThemeById = (
  themeId: string | null,
): ColorThemePreset | undefined =>
  themeId ? COLOR_THEMES.find((t) => t.id === themeId) : undefined;

export const themeFromPrimary = (primary: string): EmailTheme => {
  const preset = COLOR_THEMES.find(
    (t) => t.primary.toLowerCase() === primary.toLowerCase(),
  );
  if (preset) return preset;
  return { ...DEFAULT_THEME, primary, linkColor: primary };
};

export const stripThemeKeys = (theme: EmailTheme): EmailTheme => {
  const { id: _id, name: _name, ...rest } = theme as ColorThemePreset;
  return rest;
};
